import React, { useEffect, useState } from 'react';
import { SliderInput } from './SliderInput';
import { ROIResultCard } from './ROIResultCard';
import { PlanDropdown, PLANS } from './PlanDropdown';
import { DollarSign, Zap } from 'lucide-react';
interface ROICalculatorProps {
  'data-id'?: string;
}
/**
 * ROICalculator component
 * Simplified calculator showing what AcquireFlow will generate
 * Uses realistic 0.1% conversion rate (~1 deal per 1,000 offers)
 */
export function ROICalculator({
  'data-id': dataId
}: ROICalculatorProps) {
  // Input values
  const [profitPerDeal, setProfitPerDeal] = useState(15000);
  const [offersPerDay, setOffersPerDay] = useState(100);
  const [selectedPlan, setSelectedPlan] = useState('Core');
  // Calculated results
  const [results, setResults] = useState({
    extraDeals: 0,
    newTotalDeals: 0,
    extraProfit: 0,
    newTotalProfit: 0,
    roiPercentage: 0,
    breakEvenDeals: 0,
    planCost: 997
  });
  const [isVisible, setIsVisible] = useState(false);
  // Scroll animation
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      });
    }, {
      threshold: 0.1
    });
    const section = document.getElementById('roi-calculator-section');
    if (section) {
      observer.observe(section);
    }
    return () => {
      if (section) {
        observer.unobserve(section);
      }
    };
  }, []);
  // Calculate ROI whenever inputs change
  useEffect(() => {
    // Get plan cost
    const plan = PLANS.find(p => p.name === selectedPlan);
    const planCost = plan ? plan.price : 997;
    // Realistic conversion rate: 0.1% (~1 deal per 1,000 offers)
    // This is based on real-world data from active investors
    const conversionRate = 0.1;
    // Calculate deals from AcquireFlow offers
    const monthlyOffers = offersPerDay * 30;
    const newDeals = monthlyOffers * conversionRate / 100;
    // Calculate profit from AcquireFlow deals
    const newProfit = newDeals * profitPerDeal;
    // Calculate ROI
    const roiPercentage = planCost > 0 ? Math.floor(newProfit / planCost * 100) : 0;
    // Calculate break-even
    const breakEvenDeals = profitPerDeal > 0 ? planCost / profitPerDeal : 0;
    setResults({
      extraDeals: newDeals,
      newTotalDeals: newDeals,
      extraProfit: newProfit,
      newTotalProfit: newProfit,
      roiPercentage,
      breakEvenDeals,
      planCost
    });
  }, [profitPerDeal, offersPerDay, selectedPlan]);
  return <section id="roi-calculator-section" className="relative w-full bg-gradient-to-b from-[#121212] via-[#0f0f0f] to-[#121212] px-6 sm:px-8 lg:px-16 py-24 lg:py-32 overflow-hidden" data-id="element-2067">
      {/* Background decoration */}
      <div className="absolute inset-0 opacity-30" data-id="element-2068">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#3ab795] rounded-full blur-[120px] opacity-10" data-id="element-2069" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#3ab795] rounded-full blur-[120px] opacity-10" data-id="element-2070" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto" data-id="element-2071">
        {/* Header */}
        <div className={`text-center space-y-6 mb-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2072">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#3ab795]/10 border border-[#3ab795]/20 rounded-full backdrop-blur-sm" data-id="element-2073">
            <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-2074" />
            <span className="text-sm md:text-base font-medium text-[#3ab795] tracking-wide uppercase" data-id="element-2075">
              ROI Calculator
            </span>
          </div>

          {/* Title with teal highlight */}
          <h2 className="text-4xl md:text-6xl font-bold text-white tracking-tight leading-tight px-4" data-id="element-2076">
            See Your{' '}
            <span className="relative inline-block" data-id="element-2077">
              <span className="relative z-10 text-[#3ab795]" data-id="element-2078">
                New Monthly Revenue
              </span>
              <span className="absolute bottom-0 left-0 w-full h-3 bg-[#3ab795]/30 blur-lg -z-10" data-id="element-2079" />
            </span>
          </h2>

          {/* Subtitle */}
          <p className="text-xl md:text-2xl text-[#b8bcc8] leading-relaxed max-w-3xl mx-auto px-4" data-id="element-2080">
            One deal pays for 12 months.{' '}
            <span className="text-white font-semibold" data-id="element-2081">
              Average investor adds 2-3 extra deals in month one.
            </span>
          </p>
        </div>

        {/* Calculator Grid */}
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} data-id="element-2082">
          {/* Left Side - Inputs */}
          <div className="bg-[#18181b] border border-white/10 rounded-2xl p-6 md:p-8 space-y-6" data-id="element-2083">
            <div className="space-y-2 pb-4 border-b border-white/10" data-id="element-2084">
              <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-2085">
                Calculate Your ROI
              </h3>
              <p className="text-sm text-[#b8bcc8] leading-relaxed" data-id="element-2086">
                See how many deals AcquireFlow will generate for you
              </p>
            </div>

            {/* Input Cards - Simplified to 3 inputs */}
            <div className="space-y-4" data-id="element-2087">
              {/* Average Profit Per Deal */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6" data-id="element-2088">
                <SliderInput label="Average profit per deal" value={profitPerDeal} min={5000} max={50000} step={1000} unit="" onChange={setProfitPerDeal} icon={<DollarSign className="w-4 h-4" data-id="element-2090" />} formatValue={val => `$${(val / 1000).toFixed(0)}K`} data-id="element-2089" />
              </div>

              {/* Offers Per Day with AcquireFlow */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6" data-id="element-2091">
                <SliderInput label="Offers per day with AcquireFlow" value={offersPerDay} min={10} max={200} step={10} onChange={setOffersPerDay} icon={<Zap className="w-4 h-4" data-id="element-2093" />} data-id="element-2092" />
              </div>

              {/* Plan Selection */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-6" data-id="element-2094">
                <PlanDropdown value={selectedPlan} onChange={setSelectedPlan} data-id="element-2095" />
              </div>
            </div>
          </div>

          {/* Right Side - Results */}
          <div data-id="element-2096">
            <ROIResultCard results={results} data-id="element-2097" />
          </div>
        </div>
      </div>
    </section>;
}